import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../contexts/firebase/config';
import PageLayout from '../../components/PageLayout';

export default function AdminPartnersCreate() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [form, setForm] = useState({
    nome: '',
    morada: '',
    nif: '',
    email: '',
    contacto: '',
    password: '',
    confirmPassword: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const nome = form.nome.trim();
    const email = form.email.trim().toLowerCase();
    const password = form.password;
    const confirmPassword = form.confirmPassword;

    if (!nome || !email || !password || password !== confirmPassword) {
      setError('Verifique os campos obrigatórios e as senhas.');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter ao menos 6 caracteres.');
      return;
    }

    setLoading(true);

    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      await setDoc(
        doc(db, 'empresas', user.uid),
        {
          nomeEmpresa: nome,
          morada: form.morada || '',
          nif: form.nif || '',
          email,
          contacto: form.contacto || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          mustChangePassword: true,
        },
        { merge: true }
      );

      await setDoc(
        doc(db, 'users', user.uid),
        {
          uid: user.uid,
          email,
          nome,
          role: 'partner',
          empresa: user.uid,
          phone: form.contacto || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      navigate('/admin/parceiros');
    } catch (err) {
      console.error(err);

      setError(
        err.code === 'auth/email-already-in-use'
          ? 'E-mail já está em uso.'
          : 'Erro ao criar parceiro. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 6,
          bgcolor: 'background.default',
        }}
      >
        <Paper
          sx={{
            width: '100%',
            maxWidth: 600,
            p: 4,
            position: 'relative',
          }}
          elevation={3}
        >
          <PersonAddIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />

          <Typography variant="h5" gutterBottom>
            Novo Parceiro
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Nome *"
                name="nome"
                value={form.nome}
                onChange={handleChange}
                required
                fullWidth
              />

              <TextField
                label="Morada"
                name="morada"
                value={form.morada}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="NIF"
                name="nif"
                value={form.nif}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="E-mail *"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                fullWidth
              />

              <TextField
                label="Contacto"
                name="contacto"
                value={form.contacto}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Senha temporária *"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                helperText="Será enviada ao parceiro"
                required
                fullWidth
              />

              <TextField
                label="Confirmar senha *"
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={handleChange}
                required
                fullWidth
              />
            </Stack>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 4,
                gap: 2,
              }}
            >
              <Button
                component={RouterLink}
                to="/admin/parceiros"
                variant="outlined"
                disabled={loading}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} /> : null}
                disabled={loading}
              >
                {loading ? 'Criando...' : 'Criar Parceiro'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </PageLayout>
  );
}